<template>
  <div class="modern-form">
    <div class="template-container">
      <v-row>
        <v-col cols="12" sm="12" md="4">
          <div class="form-sidebar">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Email Templates</h3>
              <v-btn class="btn-primary btn-sm" @click="createNewTemplate">
                <v-icon left small>mdi-plus</v-icon>
                New
              </v-btn>
            </div>
            <div class="sidebar-content">
              <div class="form-list" id="emailTemplateList">
                <div v-if="templatesEmail.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-email-outline</v-icon>
                  <p class="no-results-text">No email templates</p>
                </div>
                <div v-else class="forms-list">
                  <div
                    v-for="(item, i) in templatesEmail"
                    :key="i"
                    :id="item.id"
                    @click="selectTemplate(item)"
                    class="form-item"
                    :class="{ 'selected': selectedTemplate?.id === item.id && item.type === 'email/html' }">
                    <v-icon class="form-icon">mdi-email</v-icon>
                    <span class="form-name">{{ item.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-sidebar mt-4">
            <div class="sidebar-header">
              <h3 class="sidebar-title">SMS Templates</h3>
            </div>
            <div class="sidebar-content">
              <div class="form-list" id="smsTemplateList">
                <div v-if="templatesSMS.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-message-outline</v-icon>
                  <p class="no-results-text">No SMS templates</p>
                </div>
                <div v-else class="forms-list">
                  <div
                    v-for="(item, i) in templatesSMS"
                    :key="i"
                    :id="item.id"
                    @click="selectTemplate(item)"
                    class="form-item"
                    :class="{ 'selected': selectedTemplate?.id === item.id && item.type === 'sms/text' }">
                    <v-icon class="form-icon">mdi-message-text</v-icon>
                    <span class="form-name">{{ item.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" sm="12" md="8">
          <div v-if="loading" class="loading-container">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="loading-spinner" />
          </div>
          <div v-else-if="!selectedTemplate" class="no-selection">
            <v-icon class="no-selection-icon">mdi-email-outline</v-icon>
            <p class="no-selection-text">Select a template to edit</p>
          </div>

          <div v-else class="info-section">
            <div class="section-header-with-actions">
              <h2 class="section-title">Template Configuration</h2>
            </div>

            <v-form ref="form" lazy-validation>
              <v-text-field
                label="Template Title"
                hide-details="auto"
                class="line-input custom-filled mb-4"
                placeholder="New Template"
                v-model="form.name"
                validate-on-blur
                dense
                :rules="[rules.required, rules.max50]" />

              <v-checkbox label="Enabled" v-model="form.enabled" hide-details class="mb-4" />

              <PageTabs v-bind:tabs="tabs" />

              <div class="form-actions">
                <v-btn class="btn-primary" @click="saveTemplate()">Save</v-btn>
                <v-btn class="btn-secondary" @click="clearSelection()">Cancel</v-btn>
                <v-btn class="btn-warning" :disabled="!selectedTemplate.id || selectedTemplate.id === newId" @click="showDeleteDialog = true">Delete</v-btn>
                <v-btn v-if="Number(form.id) > 0" class="btn-outline-primary" @click="showOneTimeSendDialog = true">
                  One-Time Send
                </v-btn>
              </div>
            </v-form>
          </div>
        </v-col>
      </v-row>

      <v-dialog v-model="showOneTimeSendDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Send One Time</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Are you sure you want to send this template now?</p>
            <p class="modal-description">This will immediately send to <strong>all</strong> patients matching the query criteria. Make sure you've saved any changes first.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showOneTimeSendDialog = false" :disabled="sendingInProgress">Cancel</v-btn>
            <v-btn class="btn-primary" @click="sendTemplateImmediate()" :disabled="sendingInProgress">Send</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showDeleteDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Delete Template</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Are you sure you want to delete {{ selectedTemplate?.name }}?</p>
            <p class="modal-description">This action cannot be undone.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn class="btn-warning" @click="deleteTemplate()">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>
<script>
import DataService from '../../../_services/DataService';
import Utils from '../../utils';
import ValidateService from '../../../_services/ValidateService';
import PageTabs from '../../components/PageTabs';
import Content from './editing/Content';
import Query from './editing/Query';
import Scheduling from './editing/Scheduling';

export default {
  name: 'CreateNewMessage',
  props: [],
  components: {
    PageTabs,
    Content,
    Scheduling,
  },
  data() {
    return {
      pageName: 'Create/Edit Message Templates',
      loading: true,
      form: {
        name: '',
        enabled: true,
      },
      newId: 'new',
      queries: [],
      templates: [],
      templatesEmail: [],
      templatesSMS: [],
      minHeight: '450px',
      appearance: {
        theme: 'light',
      },
      responseDialogText: '',
      validationMsgs: {
        line1: 'Form could not be submitted',
        line2: 'Please enter a valid - ',
      },
      setAlert: Utils.setAlert,
      rules: ValidateService.validators,
      showOneTimeSendDialog: false,
      sendingInProgress: false,
      showDeleteDialog: false,
      selectedTemplate: null,
      tabs: [
        {
          label: 'Content',
          section: Content,
          pageData: { parent: this },
        },
        {
          label: 'Scheduling',
          section: Scheduling,
          pageData: { parent: this },
        },
        {
          label: 'Query',
          section: Query,
          pageData: { parent: this },
        },
      ],
    };
  },
  methods: {
    closeDialog() {
      this.showOneTimeSendDialog = false;
    },
    clearSelection() {
      this.selectedTemplate = null;
      if (this.$route.query.id) {
        this.$router.replace({ query: {} });
      }
    },
    createNewTemplate() {
      this.selectedTemplate = {
        id: this.newId,
        name: 'New Template',
        cronSchedule: '',
        queryId: '',
        subjectLine: 'Please enter your subject here',
        body: '',
        category: '',
        type: 'email/html',
        enabled: true,
        json: {},
      };

      this.form = this.selectedTemplate;

      this.templates.unshift(this.selectedTemplate);
    },
    async deleteTemplate() {
      const id = this.form.id;

      const response = await DataService.deleteTemplate(id);
      if (response.status == 200) {
        this.$root.$emit('raise_alert', this.setAlert('success', 'Template successfully deleted'));

        await this.getTemplatesAll();
        this.selectedTemplate = null;
        this.showDeleteDialog = false;
        if (this.$route.query.id) {
          this.$router.replace({ query: {} });
        }
      } else {
        this.$root.$emit('raise_alert', this.setAlert('fail', 'Something went wrong; Template was not deleted'));
      }
    },
    async getTemplatesAll() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      const templateResponse = await DataService.getTemplates();
      if (templateResponse.status === 200) {
        const templateRows = templateResponse.data;
        this.templates = templateRows.map(this.displayTemplateData);
        this.setLists();
      }

      if (!this.queries.length) {
        const queryResponse = await DataService.getClientQueries();
        if (queryResponse.status === 200) {
          const queryData = queryResponse.data;
          this.queries = queryData.map(this.displayQueryData);
        }
      }
    },
    setFormByQueryData() {
      if (this.$route.query.id && this.$route.query.id != this.newId) {
        const template = this.templates.find(t => t.id == this.$route.query.id);
        if (template) {
          this.selectTemplate(template);
        }
      }
    },
    setLists() {
      this.templatesEmail = this.templates.filter((e) => e.type === 'email/html');
      this.templatesSMS = this.templates.filter((e) => e.type === 'sms/text');
    },
    async saveTemplate(disableAlert) {
      if (!this.$refs.form || !this.$refs.form.validate()) {
        return false;
      }

      const params = {
        ...this.selectedTemplate, // default to existing values
        id: this.selectedTemplate.id === this.newId ? 0 : Number(this.$route.query.id),
        name: this.form.name,
        enabled: this.form.enabled,
      };

      // handle sub tabs not loading (only edited eg the first tab)
      if (this.contentTab) {
        params.subjectLine = this.contentTab.form.subjectLine;
        params.body = this.contentTab?.form.body;
        params.type = this.contentTab?.form.messageType;
      }

      if (this.scheduleTab) {
        params.cronSchedule = this.scheduleTab.form.cronSchedule;
      }

      if (this.queryTab) {
        params.queryId = this.queryTab?.form.queryId;
        params.category = this.queryTab?.form.category.toLowerCase();
      }

      let saveSuccess;
      if (Number(params.id) > 0) {
        saveSuccess = await this.updateTemplate(params, disableAlert);
      } else {
        saveSuccess = await this.createTemplate(params, disableAlert);
      }

      return saveSuccess;
    },
    async createTemplate(params, disableAlert) {
      const createResult = await DataService.createTemplate(params);
      const createSuccess = createResult.status === 201;
      if (createSuccess) {
        if (!disableAlert) {
          this.$root.$emit('raise_alert', this.setAlert('success', 'Template has been created successfully.'));
        }

        await this.getTemplatesAll();

        this.$router.push({ query: { id: createResult.data.id } });
      } else {
        if (!disableAlert) {
          this.$root.$emit('raise_alert', this.setAlert('fail', 'New Template could not be created'));
        }
      }

      return createSuccess;
    },
    async updateTemplate(params, disableAlert) {
      const updateResult = await DataService.updateTemplate(params);
      const updateSuccess = updateResult.status === 200;
      if (updateSuccess) {
        if (!disableAlert) {
          this.$root.$emit('raise_alert', this.setAlert('success', 'Template has been updated successfully.'));
        }

        this.selectedTemplate = { ...params };

        await this.getTemplatesAll();
      } else {
        if (!disableAlert) {
          this.$root.$emit('raise_alert', this.setAlert('fail', 'Template could not be updated'));
        }
      }

      return updateSuccess;
    },
    async sendTemplateImmediate() {
      this.sendingInProgress = true;
      const sendResult = await DataService.sendTemplateImmediate(Number(this.$route.query.id));
      if (sendResult.status === 200) {
        this.$root.$emit('raise_alert', this.setAlert('success', 'Message was sent successfully'));
        this.showOneTimeSendDialog = false;
      } else {
        this.$root.$emit('raise_alert', this.setAlert('fail', 'An error occurred and the message could not be sent'));
      }

      this.sendingInProgress = false;
    },
    async goToEmailEditor() {
      const saveResult = await this.saveTemplate(true);
      if (saveResult) {
        this.$router.push({
          name: 'EmailTemplateEditor',
          query: {
            id: this.$route.query.id,
          },
        });
      }
    },
    async selectTemplate(d) {
      this.selectedTemplate = d;
      await this.$nextTick();

      if (Number(this.$route.query.id) !== d.id) {
        this.$router.push({ query: { id: d.id }});
      }

      this.form = {
        id: d.id,
        name: d.name,
        cronSchedule: d.cronSchedule ? d.cronSchedule : this.defaultCron,
        queryId: d.queryId,
        subjectLine: d.subjectLine,
        body: d.body,
        category: d.category?.charAt(0).toUpperCase() + d.category?.slice(1),
        type: d.type,
        enabled: d.enabled,
      };
    },
    displayTemplateData(d) {
      return {
        id: d.id,
        name: d.name,
        queryId: d.queryId,
        cronSchedule: d.cronSchedule,
        type: d.type,
        category: d.category,
        subjectLine: d.subjectLine,
        body: d.body,
        enabled: d.enabled,
      };
    },
    displayQueryData(d) {
      return {
        id: d.id,
        name: d.name,
        fields: d.fields,
        queryTest: d.queryText,
      };
    },
  },
  async mounted() {
    await this.getTemplatesAll();
    this.setFormByQueryData();
    this.loading = false;
  },
};
</script>
<style lang="scss" scoped>
@import '@/scss/variables.scss';

$modal-description-color: #6b7280;
$loading-bg-color: #ffffff;
$loading-border-color: #e2e8f0;
$sidebar-bg-color: #ffffff;
$sidebar-border-color: #e2e8f0;
$sidebar-title-color: #2d3748;
$no-results-icon-color: #cbd5e0;
$no-results-text-color: #718096;
$form-item-bg-color: #f8fafc;
$form-item-border-color: #e2e8f0;
$form-item-hover-bg-color: #edf2f7;
$form-item-hover-border-color: #cbd5e0;
$form-item-selected-bg-color: #f0f9e8;
$form-item-selected-border-color: #B0D35E;
$form-item-selected-text-color: #414a5b;
$form-icon-color: #133b54;
$form-name-color: #1f2937;
$no-selection-bg-color: #ffffff;
$no-selection-icon-color: #cbd5e0;
$no-selection-text-color: #718096;
$info-section-bg-color: #ffffff;
$info-section-border-color: #e2e8f0;
$section-title-color: #1f2937;
$section-border-color: #e2e8f0;
$tab-bg-color: #ffffff;

.template-container {
  padding: 36px 12px 12px 12px;
  
  ::v-deep .v-text-field {
    @media (max-width: $breakpoint-mobile) {
      .v-input__control {
        .v-input__slot {
          min-height: 48px;
          
          input {
            font-size: 16px;
          }
        }
      }
    }
  }
  
  ::v-deep .v-checkbox {
    @media (max-width: $breakpoint-mobile) {
      .v-input--selection-controls__input {
        width: 24px;
        height: 24px;
        
        .v-icon {
          font-size: 24px;
        }
      }
      
      .v-label {
        font-size: 16px;
      }
    }
  }
}

.loading-container {
  text-align: center;
  padding: 80px 24px;
  background-color: $loading-bg-color;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid $loading-border-color;
}

.loading-spinner {
  display: block;
  margin: 40px auto;
}

.form-sidebar {
  background-color: $sidebar-bg-color;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid $sidebar-border-color;
  display: flex;
  flex-direction: column;
  height: fit-content;
  
  .sidebar-header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid $sidebar-border-color;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .sidebar-title {
      font-size: 18px;
      font-weight: 600;
      color: $sidebar-title-color;
      margin: 0;
      font-family: 'Inter', sans-serif;
    }

    .btn-sm {
      padding: 6px 12px !important;
      font-size: 13px !important;
      min-width: auto !important;
    }
  }
  
  .sidebar-content {
    padding: 16px;
  }
}

.form-list {
  .no-results {
    text-align: center;
    padding: 40px 16px;
    
    .no-results-icon {
      font-size: 48px;
      color: $no-results-icon-color;
      margin-bottom: 12px;
    }
    
    .no-results-text {
      color: $no-results-text-color;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .forms-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .form-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: $form-item-bg-color;
    border-radius: 8px;
    border: 1px solid $form-item-border-color;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: $form-item-hover-bg-color;
      border-color: $form-item-hover-border-color;
    }
    
    &.selected {
      background-color: $form-item-selected-bg-color !important;
      border-color: $form-item-selected-border-color !important;
      
      .form-name {
        color: $form-item-selected-text-color !important;
        font-weight: 600 !important;
      }
    }
    
    .form-icon {
      color: $form-icon-color !important;
      margin-right: 12px !important;
      font-size: 20px !important;
    }
    
    .form-name {
      font-weight: 500 !important;
      color: $form-name-color !important;
      font-size: 14px !important;
    }
  }
}

.no-selection {
  text-align: center;
  padding: 80px 24px;
  background-color: $no-selection-bg-color;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid $sidebar-border-color;
  
  .no-selection-icon {
    font-size: 64px;
    color: $no-selection-icon-color;
    margin-bottom: 16px;
  }
  
  .no-selection-text {
    color: $no-selection-text-color;
    margin: 0;
    font-size: 16px;
  }
}

.info-section {
  background-color: $info-section-bg-color;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid $info-section-border-color;
  padding: 24px;
  
  @media (max-width: $breakpoint-mobile) {
    padding: 16px;
    border-radius: 8px;
  }
  
  ::v-deep .v-tabs {
    margin: 0 -24px 0 -24px;
    padding: 0 24px;
    min-height: 40px;
    background-color: $tab-bg-color !important;
    
    @media (max-width: $breakpoint-mobile) {
      margin: 0 -16px;
      padding: 0 8px;
      min-height: 48px;
    }
  }
  
  ::v-deep .v-tabs-bar {
    height: 40px;
    
    @media (max-width: $breakpoint-mobile) {
      height: 48px;
    }
  }
  
  ::v-deep .v-tab {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    min-width: 90px;
    padding: 0 16px;
    
    @media (max-width: $breakpoint-mobile) {
      font-size: 11px;
      letter-spacing: 0.03em;
      min-width: 0;
      padding: 0 12px;
      flex: 1;
    }
  }
  
  ::v-deep .v-tabs-slider {
    height: 3px;
    background-color: #B0D35E;
  }
  
  ::v-deep .v-window {
    padding: 24px 0 0 0;
    margin-top: 0;
    
    @media (max-width: $breakpoint-mobile) {
      padding: 16px 0 0 0;
    }
  }
  
  ::v-deep .v-window__container {
    padding-top: 0;
  }
  
  ::v-deep .v-tabs-items {
    border: none;
  }
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid $section-border-color;
  
  @media (max-width: $breakpoint-mobile) {
    margin-bottom: 16px;
    padding-bottom: 8px;
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: $section-title-color;
    margin: 0;
    border: none;
    
    @media (max-width: $breakpoint-mobile) {
      font-size: 16px;
    }
  }
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
  
  @media (max-width: $breakpoint-mobile) {
    flex-direction: column;
    gap: 8px;
    
    .v-btn {
      width: 100%;
      min-height: 48px;
      font-size: 15px;
    }
  }
}

.modal-description {
  color: $modal-description-color;
  font-size: 14px;
  margin-top: 12px;
}
</style>
